import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Button } from '../ui/button';
import { History, RotateCcw, RefreshCw } from 'lucide-react';
import { adminApi } from '../../lib/adminApi';
import { useToast } from '../../hooks/use-toast';

function fmt(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

/**
 * History dialog for a single collection+docId. Lists up to the last 50 snapshots
 * and lets an admin restore any of them (which also snapshots the current state
 * first, so the restore itself is reversible).
 *
 * Props:
 *  - collection: e.g. "about_content"
 *  - docId:      e.g. "about_main"
 *  - onRestored: callback fired after a successful restore (parent should refetch)
 *  - triggerLabel: optional custom trigger label (defaults to "History")
 */
const HistoryDialog = ({ collection, docId, onRestored, triggerLabel = 'History' }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminApi.get(`/history/${collection}/${docId}`);
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const restore = async (snapshotId) => {
    if (!window.confirm('Restore this version? Current content will be snapshotted first so this is reversible.')) return;
    setRestoringId(snapshotId);
    try {
      await adminApi.post(`/history/${collection}/${docId}/restore/${snapshotId}`);
      toast({ title: 'Restored', description: 'Previous version is now live.' });
      setOpen(false);
      onRestored?.();
    } catch {
      toast({ title: 'Restore failed', description: 'Could not restore this snapshot.', variant: 'destructive' });
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="history-open-btn">
          <History className="w-4 h-4 mr-1" /> {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5" /> Version History
          </DialogTitle>
        </DialogHeader>
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>Showing the last 50 saves for <span className="font-mono">{collection}</span></span>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
        {loading ? (
          <div className="text-center text-sm py-8 text-gray-500">Loading history…</div>
        ) : items.length === 0 ? (
          <div className="text-center text-sm py-8 text-gray-500">No previous versions yet — every save from now on will be listed here.</div>
        ) : (
          <ul className="divide-y">
            {items.map((it) => (
              <li key={it._id} className="py-3 flex items-center justify-between gap-3" data-testid={`history-item-${it._id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{fmt(it.saved_at)}</div>
                  <div className="text-xs text-gray-500 truncate">
                    {(it.note || 'save')} · by <span className="font-medium">{it.actor || 'admin'}</span>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => restore(it._id)}
                  disabled={restoringId === it._id}
                  data-testid={`history-restore-${it._id}`}
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-1" />
                  {restoringId === it._id ? 'Restoring…' : 'Restore'}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default HistoryDialog;
